/**
 * Everything the plan screens make of a `PlanRecord`: the macro arithmetic,
 * the dates written the way the design writes them, and the factories that
 * build an empty meal, option or dish row.
 *
 * It sits beside the feature rather than inside `services/` for the same
 * reason `clientMapping` does — none of it is about the wire. The service
 * hands over the record; what a screen makes of it is this.
 */

import { CATALOG_DISHES, MEAL_UNITS } from "./data/planCatalog";
import type {
  DishEntry,
  FoodUnitOption,
  Macros,
  MealOption,
  MealSlot,
  PlanRecord,
  PlanRow,
  PlanStatus,
  PlanSummary,
} from "./types/plans.types";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const EMPTY_MACROS: Macros = {
  calories: 0,
  carbs: 0,
  protein: 0,
  fat: 0,
};

// Looked up by name rather than by id: the catalog has no ids yet, and the
// dropdowns are keyed on what they display. Built once — a plan with four
// meals of five rows would otherwise scan the list twenty times per render.
const DISHES_BY_NAME = new Map(
  CATALOG_DISHES.map((dish) => [dish.name, dish])
);
const UNITS_BY_NAME = new Map(MEAL_UNITS.map((unit) => [unit.name, unit]));

/**
 * Ids for the rows, options and meals the builder creates. They never leave
 * the browser, so a counter is enough — and unlike `Math.random`, two rows
 * added in the same millisecond still differ.
 */
let idCounter = 0;
export const createId = (prefix: string): string =>
  `${prefix}-${(idCounter += 1).toString(36)}-${Date.now().toString(36)}`;

/** An empty row, which is what a dish column starts out as. */
export const createDishEntry = (): DishEntry => ({
  id: createId("dish"),
  dish: "",
  foodId: null,
  unit: "",
  unitId: null,
  unitLabel: "",
  quantity: 0,
  macros: null,
});

/**
 * A new option, opened with the one blank row it needs to be usable.
 *
 * The backend has not been told about it — that is what `isLocal` says. Every
 * option starts this way, including the blank one a meal with none of its own
 * opens on, and stops being local when `addMealOption` creates it.
 */
export const createMealOption = (): MealOption => ({
  id: createId("option"),
  isLocal: true,
  dishes: [createDishEntry()],
});

/** A meal the browser invented, with the one blank option to write in. */
export const createMealSlot = (name: string, time: string): MealSlot => ({
  id: createId("meal"),
  name,
  isLocal: true,
  time,
  options: [createMealOption()],
});

/** Minutes since midnight. Unreadable times sort to the top of the day. */
const minutesIntoDay = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return Number.isNaN(hours) || Number.isNaN(minutes) ? 0 : hours * 60 + minutes;
};

/**
 * The day in the order it is eaten.
 *
 * Meals are stored sorted rather than sorted on the way out, so the pills, the
 * record and anything later reading it all agree — a snack added at 11am
 * belongs between breakfast and lunch, not at the end because it was typed
 * last. Ties keep the order they were added in, which `sort` guarantees.
 */
export const sortMealsByTime = (meals: MealSlot[]): MealSlot[] =>
  [...meals].sort((a, b) => minutesIntoDay(a.time) - minutesIntoDay(b.time));

/* ── Macros ─────────────────────────────────────────────────────────────── */

/**
 * Whether a row is worth anything yet. A dish with no unit, or a unit with no
 * quantity, is a row someone is halfway through filling in — it counts for
 * nothing, and it is not something to save.
 */
export const isDishComplete = (entry: DishEntry): boolean =>
  !!entry.dish && !!entry.unit && !!entry.quantity;

/**
 * Whether nothing has been typed into an option yet — not one dish, unit or
 * quantity anywhere in it.
 *
 * Looser than `isDishComplete`: a row halfway through being filled in still
 * counts as something someone put there, and deleting the option would throw
 * it away. Only an option nobody has touched is empty.
 */
export const isOptionEmpty = (option: MealOption): boolean =>
  option.dishes.every(
    (dish) => !dish.dish && !dish.unit && !dish.quantity
  );

/**
 * The first meal that is not ready to be sent, or nothing if they all are.
 *
 * A plan goes to the client whole, so every meal on it has to be a meal: at
 * least one option to eat, and every option on it holding at least one row
 * that names a food, a unit and how much of it. An option left blank is a
 * heading the client would read with nothing under it, which is why it counts
 * against the meal it sits in rather than being passed over.
 *
 * Named for what it finds rather than answering yes or no, so the screen can
 * say which meal is holding the plan up instead of only that something is.
 */
export const unfilledMeal = (meals: MealSlot[]): MealSlot | undefined =>
  meals.find(
    (meal) =>
      meal.options.length === 0 ||
      !meal.options.every((option) => option.dishes.some(isDishComplete))
  );

/**
 * What one dish row adds up to.
 *
 * A row picked off the live food catalog carries its own `macros`, fetched
 * from `/foods/{id}/nutrition/` for its exact unit and quantity — that number
 * is read back as-is, since asking the API again here would only reproduce
 * the same call `MealOptionCard` already made. A row with no `macros` at all
 * (a plan written, or seeded, before the catalog endpoints landed) falls back
 * to the local per-100 g stand-in: the catalog holds macros per 100 g and
 * each unit knows what it weighs, so the row is scaled through grams — 1.5
 * katori of dal is 225 g, and 225 g is 2.25 times the per-100 g figures.
 *
 * A row missing any of its three fields, or whose live fetch has not resolved
 * yet, contributes nothing — which is what a half-filled or still-loading row
 * should do. Rounded here rather than at the end, so the totals row is the
 * sum of the numbers actually printed beside each dish.
 */
export const dishMacros = (entry: DishEntry): Macros => {
  // A custom row is priced by whoever wrote it: there is no food behind it to
  // look anything up on, so the four boxes are the answer — and they are the
  // answer from the moment they are typed, rather than waiting on a unit and a
  // quantity that no arithmetic here depends on.
  if (entry.custom) return entry.macros ?? EMPTY_MACROS;
  if (!isDishComplete(entry)) return EMPTY_MACROS;
  if (entry.macros !== undefined) return entry.macros ?? EMPTY_MACROS;

  const dish = DISHES_BY_NAME.get(entry.dish);
  const unit = UNITS_BY_NAME.get(entry.unit);
  if (!dish || !unit) return EMPTY_MACROS;

  const factor = (unit.grams * entry.quantity) / 100;
  return {
    calories: Math.round(dish.per100g.calories * factor),
    carbs: Math.round(dish.per100g.carbs * factor),
    protein: Math.round(dish.per100g.protein * factor),
    fat: Math.round(dish.per100g.fat * factor),
  };
};

/**
 * What one container of a hand-written dish is worth — the figures as they
 * were typed, before the quantity eaten multiplied them.
 *
 * A row written in the custom dish dialog carries them on `unitMacros`; one
 * read back off a plan that has been reopened may not, so they are divided
 * back out of the total, which is the same arithmetic reversed. Nothing to
 * divide by means nothing to show: an untouched row is worth zero either way.
 */
export const servingMacros = (entry: DishEntry): Macros => {
  if (entry.unitMacros) return entry.unitMacros;
  if (!entry.macros || !entry.quantity) return EMPTY_MACROS;

  const per = (total: number) => Number((total / entry.quantity).toFixed(2));
  return {
    calories: per(entry.macros.calories),
    carbs: per(entry.macros.carbs),
    protein: per(entry.macros.protein),
    fat: per(entry.macros.fat),
  };
};

/** That serving, times how many of it are eaten — what the row is worth. */
export const scaleMacros = (macros: Macros, servings: number): Macros => ({
  calories: Math.round(macros.calories * servings),
  carbs: Math.round(macros.carbs * servings),
  protein: Math.round(macros.protein * servings),
  fat: Math.round(macros.fat * servings),
});

export const sumMacros = (macros: Macros[]): Macros =>
  macros.reduce(
    (total, next) => ({
      calories: total.calories + next.calories,
      carbs: total.carbs + next.carbs,
      protein: total.protein + next.protein,
      fat: total.fat + next.fat,
    }),
    EMPTY_MACROS
  );

/** Everything on one option's list of dishes. */
export const optionMacros = (option: MealOption): Macros =>
  sumMacros(option.dishes.map(dishMacros));

/**
 * What a meal costs the day: the average of its options, not their sum.
 *
 * The options are alternatives — the client eats one of them — so summing
 * would report a day of three meals as a day of nine. Averaging instead of
 * taking the first gives the day a client can expect across whichever options
 * they happen to pick, so a heavy option and a light one both move the total.
 *
 * Options nobody has written anything into are left out of the average
 * altogether. They are a blank card waiting to be filled, not a zero-calorie
 * meal, and counting one would halve a meal's figures the moment "Add option"
 * was clicked — see `isOptionEmpty`.
 *
 * Rounded, because the totals row is written in whole numbers and two meals
 * averaging x.5 should not add up to one more than they are shown as.
 */
export const mealMacros = (meal: MealSlot): Macros => {
  const written = meal.options.filter((option) => !isOptionEmpty(option));
  if (written.length === 0) return EMPTY_MACROS;

  const total = sumMacros(written.map(optionMacros));
  return {
    calories: Math.round(total.calories / written.length),
    carbs: Math.round(total.carbs / written.length),
    protein: Math.round(total.protein / written.length),
    fat: Math.round(total.fat / written.length),
  };
};

/** The whole day, which is what the plan is judged on. */
export const planMacros = (meals: MealSlot[]): Macros =>
  sumMacros(meals.map(mealMacros));

/* ── Formatting ─────────────────────────────────────────────────────────── */

/**
 * "Bowl - 200gm" — a unit written out with what it weighs.
 *
 * A live row already carries the backend's own wording for this — see
 * `DishEntry.unitLabel` — which is read straight back rather than
 * reconstructed. Only a row the local catalog still stands in for falls back
 * to building it from `MEAL_UNITS`.
 */
export const unitLabel = (entry: DishEntry): string => {
  if (entry.unitLabel) return entry.unitLabel;
  const unit = UNITS_BY_NAME.get(entry.unit);
  return unit ? `${unit.name} - ${unit.grams}gm` : entry.unit;
};

/** Trailing zeros dropped and two decimals at most: 100.0 is "100", and an
 *  ounce's 28.3495 gm is "28.35" rather than a column of digits. */
export const formatAmount = (amount: number): string =>
  String(Number(amount.toFixed(2)));

/**
 * The same wording, for a unit the Unit field is offering rather than a row
 * that has already been written — "Cup (small) - 100gm".
 *
 * The field is where three cups have to be told apart, so the weight is on the
 * option rather than left for the summary to reveal after the fact. A unit the
 * backend sent no conversion for, and one that *is* the base unit (gm, whose
 * own weight is 1 gm), are shown by name alone — the weight would say nothing
 * the name does not.
 */
export const unitOptionLabel = (unit: FoodUnitOption): string => {
  if (unit.baseAmount == null || !unit.baseUnit) return unit.name;
  if (unit.baseAmount === 1 && unit.name === unit.baseUnit) return unit.name;
  return `${unit.name} - ${formatAmount(unit.baseAmount)}${unit.baseUnit}`;
};

/**
 * What a plan's status is called on screen.
 *
 * The app has always called a plan that has gone to the client `sent` — it is
 * what the Send plan button does, and what the Draft tab is the other half
 * of. The backend calls that same state `ACTIVE`, and so does the design's
 * status column. This is where the two words meet, so the internal name can
 * stay the one the rest of the module is written in.
 */
export const PLAN_STATUS_LABELS: Record<PlanStatus, string> = {
  draft: "Draft",
  sent: "Active",
};

/** The pill each status is shown in — the same two the client table uses. */
export const PLAN_STATUS_STYLES: Record<PlanStatus, string> = {
  draft: "bg-[#F1F4F6] text-ink-muted",
  sent: "bg-brand-soft text-[#00899B]",
};

/**
 * "00" for nothing, the number itself otherwise. The design pads a zero rather
 * than leaving a cell blank, so an untouched row still reads as a number.
 */
export const formatMacro = (value: number): string =>
  String(value).padStart(2, "0");

/**
 * Whether a box being typed into is still on its way to a number: digits with
 * at most one point, which may be trailing, or nothing at all.
 *
 * Looser than "is a number" on purpose — `1.` is a step towards `1.5`, and a
 * box that refused it would eat the point as it was typed. Every figure the
 * builder takes is typed through this rather than through `type="number"`,
 * which brings spinners and a wheel that changes the value under a scrolling
 * page. Shared by the quantity in a dish row and by the five boxes of the
 * custom dish dialog, so all of them refuse the same things.
 */
export const isNumberDraft = (text: string): boolean => /^\d*\.?\d*$/.test(text);

/**
 * A typed figure as it is written back into a box: trailing zeros dropped, and
 * nothing at all for a figure that is not there yet. `0` is not a quantity
 * anyone typed — it is what an empty row holds — so it opens the box empty
 * rather than on a zero to be deleted.
 */
export const numberDraft = (value: number | null | undefined): string =>
  value ? formatAmount(value) : "";

/**
 * `null` for anything unreadable.
 *
 * A bare `YYYY-MM-DD` is parsed as UTC midnight, which is the day before in
 * every timezone west of Greenwich. Read as a local date instead, so a plan
 * shows the day that was written rather than the day it began in.
 */
const parsePlanDate = (value: string | null): Date | null => {
  if (!value) return null;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(dateOnly ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "02 Jan, 2026" — the table's Created on column. */
export const formatPlanDate = (value: string | null): string => {
  const date = parsePlanDate(value);
  if (!date) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  return `${day} ${MONTHS[date.getMonth()]}, ${date.getFullYear()}`;
};

/**
 * "5 July 2026, Sunday" — the send dialog, which is read rather than scanned,
 * so it spells the month out and names the day.
 */
export const formatPlanDateLong = (value: string | null): string => {
  const date = parsePlanDate(value);
  if (!date) return "-";

  return `${date.getDate()} ${MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}, ${
    WEEKDAYS[date.getDay()]
  }`;
};

/**
 * A time as the meal dialog edits one: three separate values, because it is
 * picked from three lists rather than typed. A meal stores the 24-hour string;
 * these two functions are the only place that conversion happens.
 */
export interface MealTimeParts {
  /** "01" … "12". */
  hour: string;
  /** "00" … "59". */
  minute: string;
  period: "AM" | "PM";
}

/** `"14:05"` → `{ hour: "02", minute: "05", period: "PM" }`. Null if unreadable. */
export const toMealTimeParts = (time: string): MealTimeParts | null => {
  const [hours, minutes] = time.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  return {
    // Noon and midnight are both "12" on a twelve-hour clock, not "00".
    hour: String(hours % 12 === 0 ? 12 : hours % 12).padStart(2, "0"),
    minute: String(minutes).padStart(2, "0"),
    period: hours >= 12 ? "PM" : "AM",
  };
};

/** The way back: `{ "02", "05", "PM" }` → `"14:05"`. */
export const fromMealTimeParts = ({
  hour,
  minute,
  period,
}: MealTimeParts): string => {
  // 12 AM is hour zero and 12 PM is hour twelve, which is what makes this a
  // remainder rather than a straight add.
  const hour12 = Number(hour) % 12;
  const hours24 = period === "PM" ? hour12 + 12 : hour12;
  return `${String(hours24).padStart(2, "0")}:${minute}`;
};

/** "08:00 AM" from the 24-hour `HH:MM` a meal is stored with. */
export const formatMealTime = (time: string): string => {
  const parts = toMealTimeParts(time);
  return parts ? `${parts.hour}:${parts.minute} ${parts.period}` : time;
};

/* ── Records ────────────────────────────────────────────────────────────── */

/** A plan as the list table renders one. */
export const toPlanRow = (record: PlanRecord): PlanRow => ({
  id: record.id,
  clientName: record.clientName,
  planType: record.planType,
  createdOn: formatPlanDate(record.createdOn),
  macros: planMacros(record.meals),
  status: record.status,
});

/**
 * A plan held in the browser, as a row of the same list the server's plans
 * come back as — so a plan created in this sitting can sit on top of them
 * without either screen having to know which is which. Its totals are added
 * up here, because a local plan has real meals; a fetched one arrives with
 * the backend's own figures already on it.
 */
export const toPlanSummary = (record: PlanRecord): PlanSummary => ({
  id: record.id,
  name: record.name,
  clientId: record.clientId,
  clientName: record.clientName,
  planType: record.planType,
  structure: record.structure,
  createdOn: record.createdOn,
  status: record.status,
  macros: planMacros(record.meals),
});

/** A listed plan as `PlansTable` renders one — the date written out. */
export const summaryToPlanRow = (summary: PlanSummary): PlanRow => ({
  id: summary.id,
  clientName: summary.clientName,
  planType: summary.planType,
  createdOn: formatPlanDate(summary.createdOn),
  macros: summary.macros,
  status: summary.status,
});
